import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #0d9488;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 1.5rem;
  flex: 1;
`;

const FeaturesContainer = styled.div`
  margin: 4rem 0;
`;

const FeatureRow = styled.div`
  display: flex;
  margin-bottom: 3rem;
  align-items: center;
  flex-direction: column;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
  
  &:nth-child(even) {
    @media (min-width: 768px) {
      flex-direction: row-reverse;
    }
  }
`;

const FeatureImageContainer = styled.div`
  width: 100%;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    width: 45%;
    margin-bottom: 0;
  }
`;

const FeatureImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  object-fit: cover;
  max-height: 400px;
`;

const FeatureContent = styled.div`
  @media (min-width: 768px) {
    width: 50%;
    padding: 0 2rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const Training: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const services = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
          <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
          <line x1="6" y1="1" x2="6" y2="4"></line>
          <line x1="10" y1="1" x2="10" y2="4"></line>
          <line x1="14" y1="1" x2="14" y2="4"></line>
        </svg>
      ),
      title: language === 'it' ? 'CORSI' : 'COURSES',
      description: language === 'it' 
        ? 'Corsi strutturati per proprietari di immobili, manager e operatori del settore degli affitti brevi, con programmi formativi completi sulle diverse aree di competenza.'
        : 'Structured courses for property owners, managers and operators in the short-term rental sector, with comprehensive training programs on different areas of expertise.',
      path: '/services/courses'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9"></path>
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
        </svg>
      ),
      title: language === 'it' ? 'LEZIONI PRIVATE' : 'PRIVATE LESSONS',
      description: language === 'it' 
        ? 'Sessioni di coaching individuali con esperti del settore, focalizzate su esigenze specifiche e personalizzate in base alle tue necessità di business.'
        : 'Individual coaching sessions with industry experts, focused on specific needs and customized based on your business needs.',
      path: '/services/private-lessons'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      ),
      title: language === 'it' ? 'EVENTI' : 'EVENTS',
      description: language === 'it' 
        ? 'Workshop, conferenze e incontri di networking per professionisti del settore, opportunità per apprendere, condividere esperienze e creare connessioni preziose.'
        : 'Workshops, conferences and networking meetings for industry professionals, opportunities to learn, share experiences and create valuable connections.',
      path: '/services/training-events'
    }
  ];
  
  const features = [
    {
      image: 'https://images.unsplash.com/photo-1596239629893-b373987a5e55?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'ESPERTI DEL SETTORE' : 'INDUSTRY EXPERTS',
      description: language === 'it' 
        ? 'I nostri formatori sono professionisti con esperienza pratica nel campo degli affitti brevi, che combinano competenze accademiche con una profonda conoscenza delle dinamiche reali del mercato.'
        : 'Our trainers are professionals with practical experience in the field of short-term rentals, who combine academic skills with a deep knowledge of the real dynamics of the market.'
    },
    {
      image: 'https://images.unsplash.com/photo-1531545514256-b1400bc00f31?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'APPROCCIO PRATICO' : 'PRACTICAL APPROACH',
      description: language === 'it' 
        ? 'Tutte le nostre attività formative sono incentrate su casi reali e applicazioni pratiche, con strumenti, modelli e risorse che potrai utilizzare immediatamente nella tua attività.'
        : 'All our training activities are focused on real cases and practical applications, with tools, models and resources that you can use immediately in your business.'
    },
    {
      image: 'https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'FORMAZIONE CONTINUA' : 'CONTINUOUS TRAINING',
      description: language === 'it' 
        ? 'Il settore degli affitti brevi è in continua evoluzione, per questo offriamo programmi di aggiornamento continuo e accesso a risorse sempre aggiornate per i nostri clienti.'
        : 'The short-term rental industry is constantly evolving, which is why we offer continuous updating programs and access to constantly updated resources for our customers.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Formazione | J-O-H' : 'Training | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Programmi di formazione professionale per proprietari e gestori di immobili nel settore degli affitti brevi' 
            : 'Professional training programs for property owners and managers in the short-term rental industry'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'FORMAZIONE' : 'TRAINING'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'J-O-H offre programmi di formazione completi per aiutarti a massimizzare il potenziale delle tue proprietà in affitto a breve termine. Che tu sia un proprietario alle prime armi o un gestore immobiliare esperto, i nostri corsi, lezioni private ed eventi ti forniranno le competenze e le conoscenze necessarie per eccellere in questo settore in rapida evoluzione.'
              : 'J-O-H offers comprehensive training programs to help you maximize the potential of your short-term rental properties. Whether you\'re a first-time owner or an experienced property manager, our courses, private lessons and events will provide you with the skills and knowledge needed to excel in this rapidly evolving industry.'}
          </Description>
          
          <SectionTitle>
            {language === 'it' ? 'I NOSTRI SERVIZI DI FORMAZIONE' : 'OUR TRAINING SERVICES'}
          </SectionTitle>
          
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceIcon>
                  {service.icon}
                </ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <Button variant="outline" to={service.path}>
                  {language === 'it' ? 'Scopri' : 'Discover'}
                </Button>
              </ServiceCard>
            ))}
          </ServicesGrid>
          
          <SectionTitle>
            {language === 'it' ? 'PERCHÉ SCEGLIERE LA NOSTRA FORMAZIONE' : 'WHY CHOOSE OUR TRAINING'}
          </SectionTitle>
          
          <FeaturesContainer>
            {features.map((feature, index) => (
              <FeatureRow key={index}>
                <FeatureImageContainer>
                  <FeatureImage src={feature.image} alt={feature.title} />
                </FeatureImageContainer>
                <FeatureContent>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureContent>
              </FeatureRow>
            ))}
          </FeaturesContainer>
          
          <CTASection>
            <Description>
              {language === 'it' 
                ? 'Investi nel tuo successo con i nostri programmi di formazione. Acquisisci le competenze necessarie per ottimizzare le tue proprietà, aumentare i ricavi e fornire un\'esperienza eccezionale ai tuoi ospiti.'
                : 'Invest in your success with our training programs. Acquire the skills necessary to optimize your properties, increase revenue and provide an exceptional experience for your guests.'}
            </Description>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per Informazioni' : 'Contact Us for Information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Training; 